import { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { message } from "antd";
import { generateSingleImageUrl } from "store/slices/imageSlice";
import { useUpload } from "contexts/UploadContext";
import { SUCCESS_CODE, SELECTED_COUNTRY, ADD } from "constants/AppConstants";
import { IMAGE_UPLOAD_TYPES, UPLOAD_STATUS } from "constants/S3Constant";
import { uploadFileToS3 } from "utils/s3Upload";
import ImageService from "services/ImageService";
import {
  detectFolderStructure,
  generateStudentMapping,
  validateFile,
} from "utils/folderValidation";
import Utils from "utils";

export const useBackgroundUpload = (
  imageType = IMAGE_UPLOAD_TYPES.STUDENT_IMAGE_FOLDER
) => {
  const dispatch = useDispatch();
  const uploadContext = useUpload();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [folderStructure, setFolderStructure] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const uploadTasksRef = useRef([]);
  const isProcessingRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (uploadContext.isUploading) {
        uploadContext.cancelUpload();
      }
      uploadTasksRef.current = [];
      isProcessingRef.current = false;
    };
  }, []);

  const handleFolderSelect = useCallback((event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      setSelectedFiles([]);
      setValidationResult(null);
      setFolderStructure(null);
      return;
    }

    const fileObjects = Array.from(files).map((file) => ({
      file,
      relativePath: file.webkitRelativePath || file.name,
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
    }));

    const fileValidationResults = fileObjects.map((fileObj) => ({
      ...fileObj,
      validation: validateFile(fileObj.file),
    }));

    const validFileObjects = fileValidationResults.filter(
      (f) => f.validation.isValid
    );

    if (validFileObjects.length === 0) {
      message.error("No valid image files found in the selected folder");
      setSelectedFiles([]);
      setValidationResult(null);
      setFolderStructure(null);
      return;
    }

    const structureResult = detectFolderStructure(validFileObjects);

    if (!structureResult.isValid) {
      message.error(structureResult.error);
      setValidationResult(structureResult);
      setSelectedFiles([]);
      setFolderStructure(null);
      return;
    }

    const folderStructureData = generateFolderStructureDisplay(
      structureResult.validFiles,
      structureResult.detectedStructure
    );

    setSelectedFiles(validFileObjects);
    setValidationResult(structureResult);
    setFolderStructure(folderStructureData);

    // Clear any previous errors
    setUploadError(null);
    setShowErrorModal(false);

    message.success(
      `Selected ${validFileObjects.length} valid files with ${structureResult.detectedStructure} structure`
    );
  }, []);

  const generateFolderStructureDisplay = (files, structureType) => {
    const structure = {};

    try {
      files.forEach((fileObj) => {
        const pathParts = fileObj.relativePath
          ? fileObj.relativePath.split("/")
          : [];

        if (!pathParts || pathParts.length === 0) {
          console.warn("Invalid path parts for file:", fileObj.name);
          return;
        }

        let current = structure;

        pathParts.forEach((part, index) => {
          if (!part) return;

          if (index === pathParts.length - 1) {
            if (!current.files) current.files = [];
            current.files.push(fileObj);
          } else {
            if (!current[part]) {
              current[part] = {};
            }
            current = current[part];
          }
        });
      });

      return structure;
    } catch (error) {
      console.error("Error generating folder structure:", error);
      return {};
    }
  };

  const startUpload = useCallback(
    async (formData) => {
      // Prevent multiple simultaneous uploads
      if (
        uploadContext.isUploading ||
        uploadContext.isGeneratingUrls ||
        isProcessingRef.current
      ) {
        message.warning(
          "Another upload is already in progress. Please wait for it to complete."
        );
        return { success: false, error: "Upload already in progress" };
      }

      if (!selectedFiles.length || !validationResult?.isValid) {
        message.error("Please select valid files first");
        return { success: false, error: "No valid files selected" };
      }

      try {
        // Set loading state for generating URLs
        uploadContext.setGeneratingUrls(true);
        isProcessingRef.current = true;

        // Clear previous errors
        setUploadError(null);
        setShowErrorModal(false);

        const studentMapping = generateStudentMapping(
          selectedFiles,
          validationResult.detectedStructure
        );

        if (!studentMapping || Object.keys(studentMapping).length === 0) {
          throw new Error(
            "Failed to generate student mapping from selected files"
          );
        }

        const country = JSON.parse(
          localStorage.getItem(SELECTED_COUNTRY) || "{}"
        );
        const studentIds = Object.keys(studentMapping).map((studentId) => ({
          [studentId]: {
            filenames: studentMapping[studentId].filenames || [],
          },
        }));

        const jsonData = {
          country_id: country.id || country.country_id,
          school_id: formData.school_id,
          class_id: formData.class_id,
          division_id: formData.division_id,
          academic_year_id: formData.academic_year_id,
          student_ids: studentIds,
        };

        console.log("Generating presigned URLs...");
        const result = await dispatch(
          generateSingleImageUrl({
            data: jsonData,
            params: { type: imageType, mode: ADD },
          })
        ).unwrap();

        console.log("API Response:", result);

        // Check if cancelled during API call
        if (
          uploadContext.isCancelled ||
          uploadContext.cancelToken?.isCancelled
        ) {
          throw new Error("Upload cancelled during URL generation");
        }

        // Check if the result is successful
        if (result.status.status_code !== SUCCESS_CODE) {
          // Handle error case - show modal with error details
          const errorData = {
            error: result.error || "Request Failed",
            status: result.status,
            detail: result.detail,
            message: result.status?.message || result.message,
            data: result.data,
            fullResponse: result,
          };

          console.error("Upload API Error:", errorData);

          setUploadError(errorData);
          setShowErrorModal(true);

          let errorMessage =
            result.status?.message || "Failed to generate upload URLs";

          if (result.detail) {
            try {
              const detailMatch = result.detail.match(/message': '([^']+)'/);
              if (detailMatch && detailMatch[1]) {
                errorMessage = detailMatch[1];
              }
            } catch (e) {
              console.warn("Could not parse detail message:", e);
            }
          }

          throw new Error(errorMessage);
        }

        // Success case - continue with upload
        const uploadUrls = result.data?.upload_urls || [];
        if (uploadUrls.length === 0) {
          throw new Error("No upload URLs received from the API");
        }

        const uploadTasks = [];

        for (const uploadItem of uploadUrls) {
          try {
            const { student_id, file_name, url_data } = uploadItem;

            if (!student_id || !file_name || !url_data) {
              console.warn("Invalid upload item:", uploadItem);
              continue;
            }

            const { upload_url, key, public_url } = url_data;
            const studentData = studentMapping[student_id];

            if (!studentData) {
              console.warn(
                `No student data found for student_id: ${student_id}`
              );
              continue;
            }

            const fileObj = studentData.files.find((f) => {
              const fileNameWithoutExt = f.name.split(".")[0];
              const cleanFileName = Utils.formatTextClean(
                fileNameWithoutExt,
                "-"
              );
              return cleanFileName === file_name;
            });

            if (!fileObj) {
              console.warn(
                `No file found for student_id: ${student_id}, file_name: ${file_name}`
              );
              continue;
            }

            uploadTasks.push({
              id: `${student_id}_${file_name}`,
              student_id,
              file_name,
              file: fileObj.file,
              upload_url,
              s3_key: key,
              public_url,
              status: UPLOAD_STATUS.PENDING,
              progress: 0,
            });
          } catch (error) {
            console.error("Error processing upload item:", uploadItem, error);
            continue;
          }
        }

        if (uploadTasks.length === 0) {
          throw new Error("No valid upload tasks generated");
        }

        // Final cancellation check before starting
        if (
          uploadContext.isCancelled ||
          uploadContext.cancelToken?.isCancelled
        ) {
          throw new Error("Upload cancelled before starting tasks");
        }

        const taskId = Date.now().toString();
        uploadTasksRef.current = uploadTasks;

        // Start the upload process
        uploadContext.startUpload(uploadTasks, taskId);

        // Begin processing uploads
        processUploads(uploadTasks);

        message.success("Upload started successfully!");
        return { success: true };
      } catch (error) {
        console.error("Upload error:", error);

        // Only show error message if we haven't already shown the modal
        if (!showErrorModal) {
          message.error(`Upload failed: ${error.message}`);
        }

        return { success: false, error: error.message };
      } finally {
        uploadContext.setGeneratingUrls(false);
        isProcessingRef.current = false;
      }
    },
    [
      selectedFiles,
      validationResult,
      dispatch,
      imageType,
      uploadContext,
      showErrorModal,
    ]
  );

  // FIXED: processUploads with proper cancellation handling
  const processUploads = useCallback(
    async (uploadTasks) => {
      let flushTelemetry = () => {};
      isProcessingRef.current = true;

      try {
        // Upload telemetry: one event per file, flushed every 5 files and at the end.
        const telemetryBatchId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        let telemetryBuffer = [];
        flushTelemetry = (done = false) => {
          if (!telemetryBuffer.length && !done) return;
          const events = telemetryBuffer; telemetryBuffer = [];
          let client = null;
          try {
            const c = navigator.connection || {};
            client = { ua: navigator.userAgent, effectiveType: c.effectiveType || null, downlink: c.downlink || null, rtt: c.rtt || null };
          } catch (e) { client = null; }
          ImageService.uploadTelemetry({ events, done, client }).catch(() => {});
        };
        const recordTelemetry = (task, i, status, extra = {}) => {
          telemetryBuffer.push({
            batch_id: telemetryBatchId,
            index: i + 1,
            total: uploadTasks.length,
            file_name: task.file?.name,
            size_bytes: task.file?.size,
            status,
            student_id: task.student_id || task.studentId || null,
            ...extra,
          });
          if (telemetryBuffer.length >= 5) flushTelemetry(false);
        };
        for (let i = 0; i < uploadTasks.length; i++) {
          const task = uploadTasks[i];

          // Check if upload is cancelled - if so, mark ALL remaining tasks as cancelled and break
          if (
            uploadContext.isCancelled ||
            uploadContext.cancelToken?.isCancelled
          ) {
            console.log(
              "Upload cancelled - marking all remaining tasks as cancelled"
            );

            // Mark current task and all remaining tasks as cancelled
            for (let j = i; j < uploadTasks.length; j++) {
              const remainingTask = uploadTasks[j];
              if (remainingTask.status !== UPLOAD_STATUS.SUCCESS) {
                uploadContext.updateUploadStatus(
                  remainingTask.id,
                  UPLOAD_STATUS.CANCELLED
                );
              }
            }

            // Break out of the loop completely - no more uploads
            break;
          }

          // Skip if already completed, failed, or cancelled
          if (
            task.status === UPLOAD_STATUS.SUCCESS ||
            task.status === UPLOAD_STATUS.CANCELLED ||
            task.status === UPLOAD_STATUS.FAILED
          ) {
            continue;
          }

          if (!task.file) {
            uploadContext.updateUploadStatus(
              task.id,
              UPLOAD_STATUS.FAILED,
              "File not found"
            );
            continue;
          }

          try {
            // Final cancellation check before starting this specific upload
            if (
              uploadContext.isCancelled ||
              uploadContext.cancelToken?.isCancelled
            ) {
              uploadContext.updateUploadStatus(
                task.id,
                UPLOAD_STATUS.CANCELLED
              );
              // Mark all remaining tasks as cancelled too
              for (let k = i + 1; k < uploadTasks.length; k++) {
                const futureTask = uploadTasks[k];
                if (futureTask.status !== UPLOAD_STATUS.SUCCESS) {
                  uploadContext.updateUploadStatus(
                    futureTask.id,
                    UPLOAD_STATUS.CANCELLED
                  );
                }
              }
              break;
            }

            uploadContext.updateUploadStatus(task.id, UPLOAD_STATUS.UPLOADING);

            const uploadResult = await uploadFileToS3(
              task.file,
              task.upload_url,
              (progress) => {
                // Check cancellation before updating progress
                if (
                  !uploadContext.isCancelled &&
                  !uploadContext.cancelToken?.isCancelled
                ) {
                  uploadContext.updateUploadProgress(task.id, progress);
                }
              },
              (xhr) => {
                // Store XHR for potential cancellation
                uploadContext.addXhrRequest(task.id, xhr);
              },
              uploadContext.cancelToken
            );

            uploadContext.removeXhrRequest(task.id);
            recordTelemetry(task, i, "success", {
              duration_ms: uploadResult?.durationMs ?? null,
              http_status: uploadResult?.status ?? 200,
              cf_ray: uploadResult?.cfRay ?? null,
            });

            // Final check before marking as success
            if (
              !uploadContext.isCancelled &&
              !uploadContext.cancelToken?.isCancelled
            ) {
              uploadContext.updateUploadStatus(task.id, UPLOAD_STATUS.SUCCESS);
            } else {
              uploadContext.updateUploadStatus(
                task.id,
                UPLOAD_STATUS.CANCELLED
              );
            }
          } catch (error) {
            uploadContext.removeXhrRequest(task.id);

            // Check if error is due to cancellation
            if (
              uploadContext.isCancelled ||
              uploadContext.cancelToken?.isCancelled ||
              error.name === "AbortError" ||
              error.message.includes("cancelled") ||
              error.message.includes("aborted")
            ) {
              uploadContext.updateUploadStatus(
                task.id,
                UPLOAD_STATUS.CANCELLED
              );

              // If cancelled, mark all remaining tasks as cancelled and break
              for (let k = i + 1; k < uploadTasks.length; k++) {
                const futureTask = uploadTasks[k];
                if (futureTask.status !== UPLOAD_STATUS.SUCCESS) {
                  uploadContext.updateUploadStatus(
                    futureTask.id,
                    UPLOAD_STATUS.CANCELLED
                  );
                }
              }
              break;
            } else {
              console.error(`Upload failed for ${task.id}:`, error);
              recordTelemetry(task, i, "failed", {
                duration_ms: error?.durationMs ?? null,
                http_status: error?.httpStatus ?? null,
                error: String(error?.message || error).slice(0, 200),
              });
              uploadContext.updateUploadStatus(
                task.id,
                UPLOAD_STATUS.FAILED,
                error.message
              );
            }
          }
        }
      } finally {
        try { flushTelemetry(true); } catch (e) { /* ignore */ }
        isProcessingRef.current = false;
      }
    },
    [uploadContext]
  );

  const retryFailedUploads = useCallback(async () => {
    if (
      uploadContext.isUploading ||
      uploadContext.isGeneratingUrls ||
      isProcessingRef.current
    ) {
      message.warning("Please wait for current operation to complete");
      return;
    }

    const failedTasks = uploadTasksRef.current.filter((task) =>
      uploadContext.uploads.find(
        (u) => u.id === task.id && u.status === UPLOAD_STATUS.FAILED
      )
    );

    if (failedTasks.length === 0) {
      message.info("No failed uploads to retry");
      return;
    }

    const failedIds = failedTasks.map((task) => task.id);
    uploadContext.retryUpload(failedIds);

    failedTasks.forEach((task) => {
      task.status = UPLOAD_STATUS.PENDING;
      task.progress = 0;
    });

    await processUploads(failedTasks);

    message.success(`Retrying ${failedTasks.length} failed uploads`);
  }, [uploadContext, processUploads]);

  const cancelUpload = useCallback(() => {
    console.log("Cancelling all uploads...");

    // Set processing flag to false to prevent new operations
    isProcessingRef.current = false;

    // Cancel the upload context which should abort all XHR requests
    uploadContext.cancelUpload();

    // Immediately mark all pending and uploading tasks as cancelled
    if (uploadTasksRef.current.length > 0) {
      uploadTasksRef.current.forEach((task) => {
        if (
          task.status === UPLOAD_STATUS.PENDING ||
          task.status === UPLOAD_STATUS.UPLOADING
        ) {
          uploadContext.updateUploadStatus(task.id, UPLOAD_STATUS.CANCELLED);
        }
      });
    }

    message.success("All uploads cancelled successfully");
  }, [uploadContext]);

  const resetSelection = useCallback(() => {
    setSelectedFiles([]);
    setValidationResult(null);
    setFolderStructure(null);
    setUploadError(null);
    setShowErrorModal(false);
    uploadTasksRef.current = [];
    isProcessingRef.current = false;
  }, []);

  const handleCloseErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const handleRetryFromError = useCallback(() => {
    setShowErrorModal(false);
    setUploadError(null);
    message.info("Please check the folder structure and try uploading again");
  }, []);

  const isBusy =
    uploadContext.isUploading ||
    uploadContext.isGeneratingUrls ||
    isProcessingRef.current;

  return {
    selectedFiles,
    validationResult,
    folderStructure,
    uploadError,
    showErrorModal,
    handleFolderSelect,
    startUpload,
    retryFailedUploads,
    cancelUpload,
    resetSelection,
    handleCloseErrorModal,
    handleRetryFromError,
    isReady: selectedFiles.length > 0 && validationResult?.isValid && !isBusy,
    isBusy,
    isGeneratingUrls: uploadContext.isGeneratingUrls,
  };
};
